import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { Dialog, Transition } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faChevronsRight,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faBriefcase,
  faBuilding,
  faCalendar,
  faClock,
} from '@fortawesome/pro-regular-svg-icons';
import {
  DaySequence,
  DaySequenceStatus,
  DaySequenceChangeInput,
  BreakType,
} from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import { mergeSequenceGroupForDisplay } from '@core/util/daySequenceDisplayMerge.util';
import {
  millisecondsToMinutes,
  minutesToMilliseconds,
} from '@core/util/datetime.util';
import { formatUnixMsAsLocalHHmm } from '@core/util/jobTimezone.util';
import { formatTimeRangeAndDuration } from '@core/util/jobScheduleDisplay.util';
import {
  formatBreakTime,
  formatDateRangeDisplay,
  formatTime,
} from '@/utils/utils';
import Button from '@/components/Button';
import { useDaySequenceUtils } from './useDaySequenceUtils';
import TimePicker from '../shared/TimePicker';

interface DaySequenceDrawerProps {
  daySequence: DaySequence | null;
  isOpen: boolean;
  onClose: () => void;
  isEditMode?: boolean;
  onEdit?: () => void;
}

const DaySequenceDrawer = ({
  daySequence,
  isOpen,
  onClose,
  isEditMode: initialEditMode = false,
  onEdit,
}: DaySequenceDrawerProps) => {
  const { getStatusBadgeClass, getStatusLabel, getWorkerName, getClientName } =
    useDaySequenceUtils();
  const getJobById = useJobStore((state) => state.getJobById);
  const getWorkerById = useWorkerStore((state) => state.getWorkerById);
  const { resolveDaySequenceDisputes, approveDaySequences } =
    useDaySequenceStore();

  const job = daySequence?.jobId ? getJobById(daySequence.jobId) : null;
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [checkInTime, setCheckInTime] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');
  const [breakTime, setBreakTime] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Reset edit mode when drawer closes or daySequence changes
  useEffect(() => {
    if (!isOpen) {
      setIsEditMode(false);
    }
  }, [isOpen]);

  // Initialize form values when entering edit mode
  useEffect(() => {
    if (isEditMode && daySequence) {
      // Use proposed times if available, otherwise use submitted times
      const clockIn =
        daySequence.proposedClockInTime || daySequence.clockInTime;
      const clockOut =
        daySequence.proposedClockOutTime || daySequence.clockOutTime;

      if (clockIn) {
        setCheckInTime(formatUnixMsAsLocalHHmm(clockIn) || '');
      }
      if (clockOut) {
        setCheckOutTime(formatUnixMsAsLocalHHmm(clockOut) || '');
      }

      // Break durations are stored as millisecond strings; show minutes in the input.
      const breaks = daySequence.proposedBreaks || daySequence.breaks || [];
      const totalMinutes = breaks.reduce((sum, breakEvent) => {
        const durationMs = parseInt(breakEvent.duration || '0', 10);
        if (Number.isNaN(durationMs)) {
          return sum;
        }
        return sum + millisecondsToMinutes(durationMs);
      }, 0);
      setBreakTime(String(Math.round(totalMinutes)));
    }
  }, [isEditMode, daySequence, job]);

  const handleEdit = () => {
    setIsEditMode(true);
    if (onEdit) {
      onEdit();
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    // Reset form values
    if (daySequence) {
      const clockIn =
        daySequence.proposedClockInTime || daySequence.clockInTime;
      const clockOut =
        daySequence.proposedClockOutTime || daySequence.clockOutTime;
      if (clockIn) {
        setCheckInTime(formatUnixMsAsLocalHHmm(clockIn) || '');
      }
      if (clockOut) {
        setCheckOutTime(formatUnixMsAsLocalHHmm(clockOut) || '');
      }
    }
  };

  const handleSave = async () => {
    if (!daySequence || !checkInTime || !checkOutTime) return;

    setIsSaving(true);
    try {
      // Convert times to timestamps
      const startDate = daySequence.startDateISO || daySequence.startDate;
      if (!startDate) {
        console.error('No start date available');
        return;
      }

      const startDateObj = dayjs(startDate);
      const checkInDateTime = startDateObj
        .hour(parseInt(checkInTime.split(':')[0], 10))
        .minute(parseInt(checkInTime.split(':')[1], 10));
      const checkOutDateTime = startDateObj
        .hour(parseInt(checkOutTime.split(':')[0], 10))
        .minute(parseInt(checkOutTime.split(':')[1], 10));

      // User enters break time in minutes; backend stores duration as a millisecond string.
      const breakMinutes = parseInt(breakTime || '0', 10);
      const proposedBreaks =
        breakMinutes > 0
          ? [
              {
                daySequenceId: daySequence.id,
                duration: String(minutesToMilliseconds(breakMinutes)),
                type: BreakType.Unpaid,
              },
            ]
          : [];

      const changeInput: DaySequenceChangeInput = {
        id: daySequence.id,
        clockInTimestamp: checkInDateTime.valueOf().toString(),
        clockOutTimestamp: checkOutDateTime.valueOf().toString(),
        proposedBreaks,
      };

      const resolvedDaySequences = await resolveDaySequenceDisputes([
        changeInput,
      ]);
      if (resolvedDaySequences.length > 0) {
        toast.success('Timesheet dispute resolved');
      } else {
        toast.error('Failed to resolve timesheet dispute');
      }
      setIsEditMode(false);
    } catch (error) {
      console.error('Error resolving timesheet dispute:', error);
      toast.error('Failed to resolve timesheet dispute');
      setIsEditMode(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = () => {
    if (!daySequence) return;
    try {
      setIsSaving(true);
      approveDaySequences([daySequence.id]);
      onClose();
      toast.success('Timesheet approved');
    } catch (error) {
      console.error('Error approving timesheet:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!daySequence) return null;

  const displayClockSequence =
    daySequence.startDateISO != null
      ? (mergeSequenceGroupForDisplay(
          useDaySequenceStore
            .getState()
            .getDaySequenceByJobId(daySequence.jobId)
            .filter((s) => s.startDateISO === daySequence.startDateISO),
        ) ?? daySequence)
      : daySequence;

  const worker = daySequence.userId ? getWorkerById(daySequence.userId) : null;
  const workerName = getWorkerName(daySequence);
  const clientName = getClientName(daySequence);
  const initials = workerName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const isDisputed = daySequence.status === DaySequenceStatus.EscalatedToTenant;
  const isResolved = daySequence.status === DaySequenceStatus.ReadyForPay;

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/5" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={React.Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-lg">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    {/* Header */}
                    <div className="border-b border-gray-200 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          className="cursor-pointer rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                          onClick={onClose}
                        >
                          <FontAwesomeIcon
                            icon={faChevronsRight}
                            className="ml-1 h-3 w-3"
                          />
                        </button>
                        <div className="flex items-center gap-3">
                          {!isEditMode && (
                            <Link
                              to={`/jobs/${daySequence.jobId}`}
                              className="flex cursor-pointer items-center text-sm font-medium text-blue-600 hover:text-blue-700"
                            >
                              See job details
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 px-6 py-6">
                      <div className="space-y-6">
                        {/* Worker Information */}
                        <div className="flex items-center gap-3">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-600">
                            {worker?.profilePictureUrl ? (
                              <img
                                src={worker.profilePictureUrl}
                                alt={workerName}
                                className="h-12 w-12 rounded-full object-cover"
                              />
                            ) : (
                              initials
                            )}
                          </div>
                          <div className="flex-1">
                            <h2 className="text-lg font-semibold text-gray-900">
                              {workerName}
                            </h2>
                          </div>
                          <span
                            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusBadgeClass(daySequence.status)}`}
                          >
                            {getStatusLabel(daySequence.status)}
                          </span>
                        </div>

                        {/* Dispute Alert */}
                        {isDisputed && (
                          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                            <div className="flex items-start gap-3">
                              <FontAwesomeIcon
                                icon={faXmark}
                                className="mt-0.5 h-5 w-5 text-red-600"
                              />
                              <div>
                                <p className="text-sm font-medium text-red-900">
                                  Timesheet disputed
                                </p>
                                <p className="mt-1 text-sm text-red-700">
                                  {workerName} has disputed the timesheet the
                                  client proposed.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {isResolved && (
                          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                            <div className="flex items-start gap-3">
                              <FontAwesomeIcon
                                icon={faCheck}
                                className="mt-0.5 h-5 w-5 text-green-600"
                              />
                              <div>
                                <p className="text-sm font-medium text-green-900">
                                  Timesheet resolved
                                </p>
                                <p className="mt-1 text-sm text-green-700">
                                  Timesheet for {workerName} has been resolved
                                  with final times{' '}
                                  {formatTime(daySequence.clockInTime, job)} -{' '}
                                  {formatTime(daySequence.clockOutTime, job)}
                                  {formatBreakTime(daySequence.breaks) !==
                                  '0 mins'
                                    ? ` and a break lasting ${formatBreakTime(daySequence.breaks)}`
                                    : ''}
                                  .
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Job Details */}
                        {job && (
                          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <div className="flex items-center gap-2">
                              <FontAwesomeIcon
                                icon={faBriefcase}
                                className="h-4 w-4 text-gray-500"
                              />
                              <span className="text-sm font-medium text-gray-900">
                                {job.title}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FontAwesomeIcon
                                icon={faBuilding}
                                className="h-4 w-4 text-gray-500"
                              />
                              <span className="text-sm text-gray-700">
                                {clientName}
                              </span>
                            </div>
                            {job.startDate && job.endDate && (
                              <div className="flex items-center gap-2">
                                <FontAwesomeIcon
                                  icon={faCalendar}
                                  className="h-4 w-4 text-gray-500"
                                />
                                <span className="text-sm text-gray-700">
                                  {formatDateRangeDisplay(
                                    dayjs(parseInt(job.startDate, 10)),
                                    dayjs(parseInt(job.endDate, 10)),
                                  )}
                                </span>
                              </div>
                            )}
                            {job.clockInTime && job.clockOutTime && (
                              <div className="flex items-center gap-2">
                                <FontAwesomeIcon
                                  icon={faClock}
                                  className="h-4 w-4 text-gray-500"
                                />
                                <span className="text-sm text-gray-700">
                                  {formatTimeRangeAndDuration(
                                    job.clockInTime,
                                    job.clockOutTime,
                                    job,
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Timesheet Submitted */}
                        {(displayClockSequence.clockInTime ||
                          displayClockSequence.clockOutTime) && (
                          <div>
                            <h3 className="mb-3 text-sm font-semibold text-gray-900">
                              Timesheet Submitted
                            </h3>
                            <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-4">
                              {displayClockSequence.clockInTime && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Check-in
                                  </span>
                                  <span className="text-sm font-medium text-gray-900">
                                    {dayjs(
                                      parseInt(
                                        displayClockSequence.clockInTime,
                                        10,
                                      ),
                                    ).format('HH:mm')}
                                  </span>
                                </div>
                              )}
                              {displayClockSequence.clockOutTime && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Check-out
                                  </span>
                                  <span className="text-sm font-medium text-gray-900">
                                    {dayjs(
                                      parseInt(
                                        displayClockSequence.clockOutTime,
                                        10,
                                      ),
                                    ).format('HH:mm')}
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Break time
                                </span>
                                <span className="text-sm font-medium text-gray-900">
                                  {formatBreakTime(daySequence.breaks || [])}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Client Proposed */}
                        {(daySequence.proposedClockInTime ||
                          daySequence.proposedClockOutTime) && (
                          <div>
                            <h3 className="mb-3 text-sm font-semibold text-gray-900">
                              Client Proposed
                            </h3>
                            <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-4">
                              {daySequence.proposedClockInTime && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Check-in
                                  </span>
                                  <span
                                    className={`text-sm font-medium ${
                                      daySequence.clockInTime &&
                                      daySequence.proposedClockInTime !==
                                        daySequence.clockInTime
                                        ? 'text-red-600'
                                        : 'text-gray-900'
                                    }`}
                                  >
                                    {dayjs(
                                      parseInt(
                                        daySequence.proposedClockInTime,
                                        10,
                                      ),
                                    ).format('HH:mm')}
                                  </span>
                                </div>
                              )}
                              {daySequence.proposedClockOutTime && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Check-out
                                  </span>
                                  <span
                                    className={`text-sm font-medium ${
                                      daySequence.clockOutTime &&
                                      daySequence.proposedClockOutTime !==
                                        daySequence.clockOutTime
                                        ? 'text-red-600'
                                        : 'text-gray-900'
                                    }`}
                                  >
                                    {dayjs(
                                      parseInt(
                                        daySequence.proposedClockOutTime,
                                        10,
                                      ),
                                    ).format('HH:mm')}
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Break time
                                </span>
                                <span className="text-sm font-medium text-gray-900">
                                  {formatBreakTime(
                                    daySequence.proposedBreaks || [],
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Final Timesheet - Edit Mode */}
                        {isEditMode && (
                          <div>
                            <h3 className="mb-3 text-sm font-semibold text-gray-900">
                              Final timesheet
                            </h3>
                            <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
                              <div>
                                <TimePicker
                                  value={checkInTime}
                                  onChange={setCheckInTime}
                                  label="Check-in"
                                  onBlur={() => {}}
                                  incrementType="minute"
                                />
                              </div>
                              <div>
                                <TimePicker
                                  value={checkOutTime}
                                  onChange={setCheckOutTime}
                                  label="Check-out"
                                  onBlur={() => {}}
                                  incrementType="minute"
                                />
                              </div>
                              <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">
                                  Break time
                                </label>
                                <input
                                  type="number"
                                  value={breakTime}
                                  onChange={(e) => setBreakTime(e.target.value)}
                                  placeholder="Input break time"
                                  className="w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                                  min="0"
                                />
                                <span className="mt-1 text-xs text-gray-500">
                                  Enter break time in minutes
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="border-t border-gray-200 px-6 py-4">
                      {isEditMode ? (
                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            onClick={handleCancel}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            onClick={handleSave}
                            disabled={isSaving || !checkInTime || !checkOutTime}
                            className="flex-1"
                          >
                            {isSaving
                              ? 'Saving...'
                              : 'Resolve disputed timesheet'}
                          </Button>
                        </div>
                      ) : isResolved ? (
                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            onClick={handleEdit}
                            disabled={isSaving}
                            className="flex-1"
                          >
                            Edit timesheet
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex gap-3">
                            <Button
                              variant="outline"
                              onClick={handleEdit}
                              disabled={isSaving}
                              className="flex-1"
                            >
                              Edit timesheet
                            </Button>
                            <Button
                              variant="primary"
                              onClick={handleApprove}
                              disabled={isSaving}
                              className="flex-1"
                            >
                              Approve proposed timesheet
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DaySequenceDrawer;
